import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ScannerSection, closeCameraAfterScanFlash, type ScannerSectionHandle } from '../../scanner';
import { isFadingQrScanError } from '../../scanner/qrScanMessages';
import { fetchPrefixMap, fetchBundleWorkShelfLocations } from '../../../services/skuShelfIndexAPI';
import { fetchSkusWithNotes } from '../../../services/productWarehouseNotesAPI';
import { getLabelStatus, getLabelByStorageIdPrefix } from '../../../services/shelfIndexAPI';
import {
  applyBundleFloorBinDeltas,
  fetchBundleFloorLevelBySku,
  type BundleFloorBinDelta,
} from '../../../services/bundleFloorAPI';
import {
  buildBundleScanSubsetBomMap,
  handleQrCapture as handleQrCaptureCore,
  targetedScanFromPickItem,
  type BundleScanSubsetBomMap,
  type ScanMatchResult,
  type TargetedScan,
} from './scanningLogic';
import {
  BUNDLE_PICK_VIEW,
  buildBundleWorkOrderItems,
  bundleWorkItemsForPickView,
  bundleWorkOrderSessionId,
  bundlesToMakeOnFloor,
  getBundleWorkShelfSkus,
  recordBundleWorkSourceScan,
  resolvePrimaryPickBinPrefix,
  resolvePrimaryPickBinShelf,
  formatBundleWorkPickPrompt,
  type BundleBinSplitEntry,
  type BundlePickView,
} from './bundleWorkOrderLogic';
import { normalizeSku } from '../../../utils/normalizeSku';
import { stripByKink } from '@/shared/utils/stripByKink';
import { handleReset as handleResetCore } from './orderDataLogic';
import { usePickSession } from './usePickSession';
import {
  loadBundleWorkSourceScans,
  markOrderItemsScanned,
  unscanOrderItems,
  writeBundleWorkSourceScans,
  type BundleWorkSourceScan,
} from '../../../utils/scanSessionCache';
import { usePickListShelfData } from './usePickListShelfData';
import useVisibleItems from './useVisibleItems';
import { usePickViewTouchLock } from './usePickViewTouchLock';
import { useAutoOpenOrderDetailsCamera } from './useAutoOpenOrderDetailsCamera';
import { useUserSettings } from '../../../contexts/UserSettingsContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useHybridShelfIndexSync } from '../../../hooks/useHybridShelfIndexSync';
import { useBundleBaselineUpdate } from '../../../hooks/useBundleBaselineUpdate';
import { getApiErrorMessage } from '../../../services/errorUtils';
import { parseBundleWorkNavState } from './bundleWorkNavState';
import ProductInfoModal from '../../shared/ProductInfoModal';
import type { OrderItem } from '../../../types/apiTypes';
import type { BundleFloorLevel } from '../../../schemas/apiResponse.schemas';
import BundleWorkNavbar from './BundleWorkNavbar';
import OrderDetailCameraBar, { CameraIcon, PICK_SCAN_BAR_VARIANTS } from './OrderDetailCameraBar';
import OrderActionsMenu from './OrderActionsMenu';
import type { PickScanBarVariant } from './OrderDetailCameraBar';
import ComponentPickList from './ComponentPickList';
import BundleWorkCompleteModal from './BundleWorkCompleteModal';
import BundleWorkReadySheet from './BundleWorkReadySheet';
import BundleWorkReturnView from './BundleWorkReturnView';
import BundleWorkListHeading from './BundleWorkListHeading';
import BundleWorkBaselineModal from './BundleWorkBaselineModal';
import { IngredientsIcon, TotalIcon } from './BundleWorkPickViewIcons';
import { applyShelfMapToOrderItems, loadOrderShelfLocationMap } from './shelfUtils';
import './pick-list.css';

function resolvePickBin(
  parentSku: string,
  shelfLocationsMap: Readonly<Record<string, { shelfIndex?: string | null | undefined }>>,
): string {
  const parentSkuKey = parentSku.toUpperCase();
  return shelfLocationsMap[parentSku]?.shelfIndex
    ?? shelfLocationsMap[parentSkuKey]?.shelfIndex
    ?? 'N/A';
}

function BundleWorkOrderDetail(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useUserSettings();
  const { isAdmin } = useAuth();
  const cameraOpensOrderDetails = settings.cameraOpensOrderDetails;
  const navState = useMemo(() => parseBundleWorkNavState(location.state), [location.state]);
  const { parentSku: parentSkuParam } = useParams();
  const parentSku = decodeURIComponent(parentSkuParam ?? '');
  const sessionKey = useMemo(() => bundleWorkOrderSessionId(parentSku), [parentSku]);
  const { saveScannedItems, loadScannedItems, clearScannedItems, scanSessionEpoch } = usePickSession(sessionKey);

  const [pickItems, setPickItems] = useState<OrderItem[]>([]);
  const [pickBin, setPickBin] = useState('…');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [isReadyForSubmit, setIsReadyForSubmit] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedProductInfo, setSelectedProductInfo] = useState<OrderItem | null>(null);
  const [webcamOn, setWebcamOn] = useState(false);
  const [prefixToSkuMap, setPrefixToSkuMap] = useState<Record<string, string>>({});
  const [subsetBomMap, setSubsetBomMap] = useState<BundleScanSubsetBomMap>(() => new Map());
  const [floorEntry, setFloorEntry] = useState<BundleFloorLevel | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);
  const [completeModalError, setCompleteModalError] = useState<string | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [pickView, setPickView] = useState<BundlePickView>(BUNDLE_PICK_VIEW.Ingredients);
  const [showBaselineModal, setShowBaselineModal] = useState(false);
  const baselineUpdate = useBundleBaselineUpdate(parentSku);
  const [sourceScans, setSourceScans] = useState<readonly BundleWorkSourceScan[]>([]);

  const scannerCaptureRef = useRef<ScannerSectionHandle>(null);
  const shelfScanTargetRef = useRef<TargetedScan | null>(null);
  const successFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const needsShelfRetryRef = useRef(false);
  const { getVisibleItems, observeItem } = useVisibleItems();

  const shelfSkus = useMemo(
    () => (loading || pickItems.length === 0 ? [] : getBundleWorkShelfSkus(parentSku, pickItems)),
    [loading, parentSku, pickItems],
  );
  const fetchWorkOrderLocations = useCallback(
    (skus: readonly string[]) => fetchBundleWorkShelfLocations(parentSku, skus),
    [parentSku],
  );
  const {
    shelfLocationsMap,
    skusWithNotes,
    applyShelfDataToItems,
    refreshShelfData,
    hydrateShelfData,
  } = usePickListShelfData(shelfSkus, { autoFetch: false, fetchLocations: fetchWorkOrderLocations });

  useEffect(() => () => { isMountedRef.current = false; }, []);

  usePickViewTouchLock();

  const isSuccessMessage = (msg: string): boolean =>
    msg === 'Work order complete!' ||
    msg === 'Order reset successfully' ||
    (msg.startsWith('SKU ') && msg.includes('matched'));

  const bundleWorkMessageClass = (msg: string): string => {
    if (isSuccessMessage(msg)) return 'order-detail-message order-detail-message-success';
    if (msg.includes('Processing image') || msg.includes('Looking up label')) {
      return 'order-detail-message order-detail-message-default';
    }
    if (isFadingQrScanError(msg)) return 'order-detail-message order-detail-message-error webcam-qr-miss-fade';
    return 'order-detail-message order-detail-message-error';
  };

  useEffect(() => {
    if (isSuccessMessage(message) || isFadingQrScanError(message)) {
      successFadeTimerRef.current = setTimeout(() => setMessage(''), 4000);
      return () => {
        if (successFadeTimerRef.current) {
          clearTimeout(successFadeTimerRef.current);
        }
      };
    }
    return undefined;
  }, [message]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setMessage('');
    setPickItems([]);
    setPickBin('…');
    setFloorEntry(null);
    setIsSubmitted(false);
    setWebcamOn(false);
    setShowCompleteModal(false);
    setCompleteModalError(null);
    setActionMenuOpen(false);
    setPickView(BUNDLE_PICK_VIEW.Ingredients);
    setShowBaselineModal(false);
    setSourceScans([]);

    const load = async (): Promise<void> => {
      try {
        const prefetchedFloor = parseBundleWorkNavState(location.state).floorEntry;
        let loadedFloor: BundleFloorLevel;
        if (
          prefetchedFloor &&
          prefetchedFloor.sku.toUpperCase() === parentSku.toUpperCase()
        ) {
          loadedFloor = prefetchedFloor;
        } else {
          loadedFloor = await fetchBundleFloorLevelBySku(parentSku);
        }
        if (cancelled) return;

        setFloorEntry(loadedFloor);

        const components = loadedFloor.components ?? [];
        setSubsetBomMap(
          buildBundleScanSubsetBomMap(
            components.length > 0 ? [{ parentSku, components }] : [],
          ),
        );

        const baseItems = buildBundleWorkOrderItems(parentSku, components);
        const allShelfSkus = getBundleWorkShelfSkus(parentSku, baseItems);
        const [prefixMap, notesMap] = await Promise.all([
          fetchPrefixMap([parentSku]),
          fetchSkusWithNotes(allShelfSkus),
        ]);
        const { map: shelfMap, loaded: shelfLocationsLoaded } = await loadOrderShelfLocationMap(
          allShelfSkus,
          (skus) => fetchBundleWorkShelfLocations(parentSku, skus),
        );
        if (cancelled) return;

        if (shelfLocationsLoaded) {
          hydrateShelfData(shelfMap, notesMap);
        } else {
          hydrateShelfData({}, notesMap);
          needsShelfRetryRef.current = true;
        }
        const itemsWithShelf = applyShelfMapToOrderItems(baseItems, shelfMap, shelfLocationsLoaded);

        setPrefixToSkuMap(prefixMap);
        setPickBin(resolvePickBin(parentSku, shelfMap));
        setPickItems(loadScannedItems(itemsWithShelf));
        setSourceScans(loadBundleWorkSourceScans(sessionKey));
      } catch (error: unknown) {
        if (cancelled) return;
        console.error('Error loading bundle work order:', error);
        setMessage(getApiErrorMessage(error, 'Error loading bundle work order. Please try again.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [parentSku, sessionKey, location.key, location.state, loadScannedItems, hydrateShelfData]);

  useEffect(() => {
    if (pickItems.length === 0) {
      return;
    }
    setPickItems((prev) => {
      const next = applyShelfDataToItems(prev);
      const changed = next.some((item, index) => {
        const prior = prev[index];
        return !prior || item.shelfIndex !== prior.shelfIndex || item.shelfDisplay !== prior.shelfDisplay;
      });
      return changed ? next : prev;
    });
  }, [applyShelfDataToItems, pickItems.length]);

  useEffect(() => {
    setPickBin(resolvePickBin(parentSku, shelfLocationsMap));
  }, [parentSku, shelfLocationsMap]);

  useEffect(() => {
    if (isSubmitted || pickItems.length === 0) {
      return;
    }
    saveScannedItems(pickItems);
    writeBundleWorkSourceScans(sessionKey, sourceScans);
  }, [pickItems, saveScannedItems, isSubmitted, sessionKey, sourceScans]);

  useEffect(() => {
    if (scanSessionEpoch === 0) {
      return;
    }
    setPickItems((prev) => {
      if (!prev.some((item) => item.scanned)) {
        return prev;
      }
      return unscanOrderItems(prev);
    });
    setSourceScans([]);
  }, [scanSessionEpoch]);

  const refreshPickShelfData = useCallback(async (): Promise<void> => {
    if (!isMountedRef.current) return;
    try {
      await refreshShelfData();
    } catch (error: unknown) {
      console.error('Error refreshing bundle work shelf data:', error);
      return;
    }
    setPickItems((prev) => applyShelfDataToItems(prev));
  }, [refreshShelfData, applyShelfDataToItems]);

  useEffect(() => {
    if (!needsShelfRetryRef.current || loading) {
      return;
    }
    needsShelfRetryRef.current = false;
    void refreshPickShelfData();
  }, [loading, refreshPickShelfData]);

  useHybridShelfIndexSync({
    enabled: !loading && pickItems.length > 0,
    onSync: refreshPickShelfData,
    pollMs: 0,
    tables: ['shelf_indexes', 'product_warehouse_notes'],
  });

  useEffect(() => {
    const allPickLinesScanned =
      pickItems.length > 0 && pickItems.every((item) => item.scanned);
    setIsReadyForSubmit(allPickLinesScanned);
    if (!allPickLinesScanned) return undefined;
    const timer = closeCameraAfterScanFlash(setWebcamOn);
    return () => clearTimeout(timer);
  }, [pickItems]);

  useAutoOpenOrderDetailsCamera({
    sessionKey,
    loading,
    enabled: cameraOpensOrderDetails,
    itemCount: pickItems.length,
    allPickLinesScanned: pickItems.length > 0 && pickItems.every((item) => item.scanned),
    isSubmitted,
    setWebcamOn,
  });

  const recordSourceScan = useCallback((scanResult: ScanMatchResult): void => {
    const label = scanResult.scannedLabel;
    if (!label) return;
    setSourceScans((prev) => recordBundleWorkSourceScan(prev, label, parentSku));
  }, [parentSku]);

  const consumeShelfScanTarget = (): TargetedScan | undefined => {
    const target = shelfScanTargetRef.current ?? undefined;
    shelfScanTargetRef.current = null;
    return target;
  };

  const handleSelectShelf = (item: OrderItem): void => {
    if (!webcamOn) {
      return;
    }
    const target = targetedScanFromPickItem(item);
    if (!target) {
      return;
    }
    shelfScanTargetRef.current = target;
    scannerCaptureRef.current?.capture();
  };

  const handleQrCapture = async (qrData: string | null): Promise<boolean> => {
    const target = consumeShelfScanTarget();
    if (!qrData) return false;
    const scanResult = await handleQrCaptureCore(
      qrData,
      prefixToSkuMap,
      getLabelStatus,
      getLabelByStorageIdPrefix,
      pickItems,
      setPickItems,
      setMessage,
      getVisibleItems(),
      true,
      subsetBomMap,
      parentSku,
      target,
    );
    recordSourceScan(scanResult);
    return scanResult.matched;
  };

  const handleSubmit = (): void => {
    clearScannedItems();
    setMessage('');
    setWebcamOn(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 1000);
    setIsSubmitted(true);
    setShowCompleteModal(false);
    setCompleteModalError(null);
    setActionMenuOpen(false);
  };

  const handleReset = (): void => {
    handleResetCore(pickItems, setPickItems, setMessage, setIsReadyForSubmit);
    setSourceScans([]);
    setShowCompleteModal(false);
    setCompleteModalError(null);
  };

  const handleMarkScanned = (): void => {
    setActionMenuOpen(false);
    setPickItems((prev) => markOrderItemsScanned(prev));
  };

  const handleOpenCompleteModal = (): void => {
    setCompleteModalError(null);
    setShowCompleteModal(true);
  };

  const handleCancelCompleteModal = (): void => {
    if (completeSubmitting) {
      return;
    }
    setShowCompleteModal(false);
    setCompleteModalError(null);
  };

  /**
   * Counted on-hand per pick bin replaces the previous quantity. Source-material
   * scans stay in memory for the return list; they are not floor deltas.
   */
  const handleConfirmComplete = async (
    entries: readonly BundleBinSplitEntry[],
  ): Promise<void> => {
    setCompleteSubmitting(true);
    setCompleteModalError(null);
    try {
      const deltas: BundleFloorBinDelta[] = entries.map((entry) => ({
        sku: parentSku,
        storageIdPrefix: entry.storageIdPrefix,
        quantity: entry.quantity,
        direction: 'set_quantity' as const,
      }));

      if (deltas.length > 0) {
        const batch = await applyBundleFloorBinDeltas(deltas, 'bundle-work-complete');
        const failed = batch.results.filter((result) => !result.applied);
        if (failed.length > 0) {
          setCompleteModalError(
            failed[0].error ?? 'Failed to update floor quantity. Please try again.',
          );
          return;
        }
        const updated = batch.entries.find(
          (entry) => entry.sku.toUpperCase() === parentSku.toUpperCase(),
        );
        if (updated) {
          setFloorEntry((prev) => (prev ? { ...prev, ...updated } : updated));
        }
      }
      handleSubmit();
    } catch (error: unknown) {
      console.error('Error recording bundles made:', error);
      setCompleteModalError(getApiErrorMessage(error, 'Failed to update floor quantity. Please try again.'));
    } finally {
      setCompleteSubmitting(false);
    }
  };

  const handleOpenBaseline = (): void => {
    setActionMenuOpen(false);
    baselineUpdate.clearError();
    setShowBaselineModal(true);
  };

  const handleConfirmBaseline = async (baselineQty: number): Promise<void> => {
    const updated = await baselineUpdate.submit(baselineQty);
    if (!updated) {
      return;
    }
    setFloorEntry((prev) => {
      if (!prev) {
        return updated;
      }
      return {
        ...prev,
        ...updated,
        itemName: updated.itemName ?? prev.itemName,
        components: updated.components ?? prev.components,
        bins: updated.bins ?? prev.bins,
      };
    });
    setShowBaselineModal(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 1000);
  };

  const barVariant: PickScanBarVariant = isSubmitted
    ? PICK_SCAN_BAR_VARIANTS.NAV
    : isReadyForSubmit
      ? PICK_SCAN_BAR_VARIANTS.SUBMIT
      : PICK_SCAN_BAR_VARIANTS.CAMERA;
  const pickViewActions = (
    <div className="order-detail-bottom-bar-left-group bwo-pick-view-toggles">
      <button
        type="button"
        className={`bwo-pick-view-btn${pickView === BUNDLE_PICK_VIEW.Total ? ' bwo-pick-view-btn-active' : ''}`}
        aria-pressed={pickView === BUNDLE_PICK_VIEW.Total}
        aria-label="Show totals for all bundles"
        onClick={() => setPickView(BUNDLE_PICK_VIEW.Total)}
      >
        <TotalIcon />
      </button>
      <button
        type="button"
        className={`bwo-pick-view-btn${pickView === BUNDLE_PICK_VIEW.Ingredients ? ' bwo-pick-view-btn-active' : ''}`}
        aria-pressed={pickView === BUNDLE_PICK_VIEW.Ingredients}
        aria-label="Show ingredients for one bundle"
        onClick={() => setPickView(BUNDLE_PICK_VIEW.Ingredients)}
      >
        <IngredientsIcon />
      </button>
    </div>
  );
  const leftActions =
    barVariant === PICK_SCAN_BAR_VARIANTS.NAV ? (
      <button
        type="button"
        onClick={() => navigate('/bundle-work-orders')}
        className="order-detail-bottom-bar-nav-btn"
      >
        Bundle Work Orders
      </button>
    ) : pickViewActions;
  const centerActions =
    barVariant === PICK_SCAN_BAR_VARIANTS.CAMERA ? (
      <button
        type="button"
        className="order-detail-bar-camera-btn order-detail-bar-camera-btn-center"
        onClick={() => setWebcamOn(true)}
        aria-label="Open camera"
      >
        <CameraIcon size={28} />
      </button>
    ) : barVariant === PICK_SCAN_BAR_VARIANTS.SUBMIT ? (
      <button
        type="button"
        onClick={handleOpenCompleteModal}
        disabled={!isReadyForSubmit}
        className={`order-detail-submit-btn ${isReadyForSubmit ? 'active' : ''}`}
      >
        Complete Work Order
      </button>
    ) : undefined;
  const rightActions = isSubmitted
    ? undefined
    : (
      <OrderActionsMenu
        open={actionMenuOpen}
        onToggle={() => setActionMenuOpen((prev) => !prev)}
        actionName="Bundle"
        onChangeBaseline={handleOpenBaseline}
        onMarkScanned={isAdmin ? handleMarkScanned : undefined}
        onReset={() => {
          setActionMenuOpen(false);
          handleReset();
        }}
      />
    );
  const showReadySheet = isReadyForSubmit && !isSubmitted && !webcamOn;
  const readyBundlesToMake = floorEntry ? bundlesToMakeOnFloor(floorEntry) : 0;
  const displayPickItems = useMemo(
    () => bundleWorkItemsForPickView(pickItems, pickView, readyBundlesToMake),
    [pickItems, pickView, readyBundlesToMake],
  );
  const pickPrompt = formatBundleWorkPickPrompt(readyBundlesToMake, pickView);
  const parentReturnShelf = resolvePrimaryPickBinShelf(floorEntry?.bins, pickBin);
  const parentReturnLabelId = resolvePrimaryPickBinPrefix(floorEntry?.bins);
  const parentReturnName = useMemo(() => {
    const raw = floorEntry?.itemName?.trim();
    if (!raw) {
      return null;
    }
    return stripByKink(raw) || raw;
  }, [floorEntry?.itemName]);
  const returnNameBySku = useMemo(() => {
    const map: Record<string, string> = {};
    const add = (sku: string, name: string | null | undefined): void => {
      const key = normalizeSku(sku);
      const trimmed = name?.trim();
      if (!key || !trimmed || map[key]) {
        return;
      }
      map[key] = stripByKink(trimmed) || trimmed;
    };
    add(parentSku, floorEntry?.itemName);
    floorEntry?.components?.forEach((component) => add(component.sku, component.name));
    pickItems.forEach((item) => add(item.sku, item.name));
    return map;
  }, [floorEntry, parentSku, pickItems]);

  return (
    <div
      className={`order-detail-container${webcamOn ? ' order-detail-camera-open' : ''}${showReadySheet ? ' order-detail-complete-open' : ''}`}
    >
      {showSuccess ? (
        <div className="success-flash">
          <div className="success-message">✓ Success!</div>
        </div>
      ) : null}

      <BundleWorkNavbar
        parentSku={parentSku}
        navState={navState}
      />

      {message && !webcamOn ? (
        <div className="order-detail-flash" aria-live="polite">
          <div className={bundleWorkMessageClass(message)}>
            {message}
          </div>
        </div>
      ) : null}

      {isSubmitted ? (
        <BundleWorkReturnView
          parentSku={parentSku}
          parentName={parentReturnName}
          parentLabelId={parentReturnLabelId}
          parentShelf={parentReturnShelf}
          sourceScans={sourceScans}
          nameBySku={returnNameBySku}
          onPrintLabels={() => navigate('/print-warehouse-labels/print')}
        />
      ) : loading ? (
        <h1 className="bundle-work-loading">Loading...</h1>
      ) : (
        <ComponentPickList
          orderItems={displayPickItems}
          skusWithNotes={skusWithNotes}
          observeItem={observeItem}
          onSelectItem={setSelectedProductInfo}
          onSelectShelf={handleSelectShelf}
          shelfScanEnabled={webcamOn}
          locationSort="bundle-work-source"
          header={(
            <BundleWorkListHeading subtitle={pickPrompt.subtitle}>
              {pickPrompt.title}
            </BundleWorkListHeading>
          )}
        />
      )}

      {selectedProductInfo ? (
        <ProductInfoModal
          item={selectedProductInfo}
          onClose={() => setSelectedProductInfo(null)}
        />
      ) : null}

      <BundleWorkBaselineModal
        open={showBaselineModal}
        baselineQty={floorEntry?.targetQty ?? 0}
        submitting={baselineUpdate.submitting}
        error={baselineUpdate.error}
        onCancel={() => {
          if (!baselineUpdate.submitting) {
            setShowBaselineModal(false);
          }
        }}
        onConfirm={(qty) => { void handleConfirmBaseline(qty); }}
      />

      <BundleWorkCompleteModal
        open={showCompleteModal}
        parentSku={parentSku}
        bins={floorEntry?.bins ?? []}
        suggestedQty={floorEntry ? bundlesToMakeOnFloor(floorEntry) : null}
        submitting={completeSubmitting}
        error={completeModalError}
        onCancel={handleCancelCompleteModal}
        onConfirm={(entries) => { void handleConfirmComplete(entries); }}
      />

      <OrderDetailCameraBar
        open={webcamOn}
        onShutter={() => scannerCaptureRef.current?.capture()}
        barVariant={barVariant}
        leftActions={leftActions}
        centerActions={centerActions}
        rightActions={rightActions}
        picked={isReadyForSubmit && !isSubmitted}
        completeActions={(
          <BundleWorkReadySheet
            parentSku={parentSku}
            bundlesToMake={readyBundlesToMake}
          />
        )}
      >
        <ScannerSection
          ref={scannerCaptureRef}
          mode="qr"
          onCapture={handleQrCapture}
          webcamOn={webcamOn}
          setWebcamOn={setWebcamOn}
          className="webcam-capture-container"
          overlay={
            webcamOn && message ? (
              <div className={bundleWorkMessageClass(message)} role="status">
                {message}
              </div>
            ) : null
          }
          cornerAction={
            <button
              type="button"
              className="webcam-viewport-close-btn"
              onClick={(event) => {
                event.stopPropagation();
                setWebcamOn(false);
              }}
              aria-label="Close camera"
            >
              ×
            </button>
          }
        />
      </OrderDetailCameraBar>
    </div>
  );
}

export default BundleWorkOrderDetail;
